import get from 'lodash/get';
import find from 'lodash/find';
import { createSelector } from 'reselect';
import { timeSlotChatValueSelector } from '../timeSlotChat/selectors';
import getAvailableAppealDeadline from '../../services/getAvailableAppealDeadline';
import { getConvertedExamType } from '../serializers/exam';

export const examSelector = state => state.examData;

export const examNameSelector = createSelector(
  examSelector,
  subState => subState.examName
);

export const examIdSelector = createSelector(
  examSelector,
  subState => subState.examId
);

/**
 *  Возвращает флаг показа только неправильных ответов
 */
export const showOnlyIncorrectSelector = createSelector(
  examSelector,
  subState => subState.showOnlyIncorrectByGrade
);

/**
 *  Возвращает список экзаменов
 */
export const examListSelector = createSelector(examSelector, subState =>
  subState.examList.filter(
    item => !!item.appeal_start_date || !!item.appeal_start_date2
  )
);

/**
 *  Возвращает лоадер экзаменов
 */
export const examDataLoaderSelector = createSelector(
  examSelector,
  subState => subState.loading
);

/**
 *  Возвращает ошибку при неверной паре код/номер
 */
export const invalidExamAttributesSelector = createSelector(
  examSelector,
  subState => subState.invalidExamAttributes
);

/**
 *  Возвращает флаг согласия с условиями подачи аппеляции
 */
export const appealAgreementOnSelector = createSelector(
  examSelector,
  subState => subState.agreementOn
);

export const appealResultEmptyDatesSelector = createSelector(
  examSelector,
  examListSelector,
  (subState, examList) => {
    return !examList.length && !!subState.examList.length;
  }
);

/**
 *  Возвращает флаг неготовых результатов
 */
export const appealResultNotReadySelector = createSelector(
  examSelector,
  appealResultEmptyDatesSelector,
  (subState, appealResultEmptyDates) =>
    subState.resultNotReady || appealResultEmptyDates
);

/**
 *  Возвращает выбранный экзамен
 */
export const currentExamSelector = createSelector(
  examListSelector,
  examIdSelector,
  (examList, examId) => find(examList, { examId }) || {}
);

/**
 * Возвращает набор выбранных заданий из экзамена по которым подается аппеляция
 */
export const appealGradesExamSelector = createSelector(
  currentExamSelector,
  subState =>
    get(subState, 'grades_table', []).filter(
      grade => grade.checked && grade.comment && !grade.invalid
    )
);

/**
 * Возвращает набор выбранных заданий из экзамена по которым подается аппеляция без учета комментария и валидности
 */
export const appealGradesAllExamSelector = createSelector(
  currentExamSelector,
  subState => get(subState, 'grades_table', []).filter(grade => grade.checked)
);

/**
 * Возвращает набор выбранных заданий из экзамена
 */
export const allCheckedGradesSelector = createSelector(
  currentExamSelector,
  subState => get(subState, 'grades_table', []).filter(grade => grade.checked)
);
/**
 * Возвращает флаг о прокомментированости отмеченых заданий
 */
export const allCheckedGradesCommentedSelector = createSelector(
  currentExamSelector,
  allCheckedGradesSelector,
  (subState, grades) => grades.length && !find(grades, grade => !grade.comment)
);

/**
 * Возвращает текущий период апелляции для выбранного экзамена, если есть актуальный
 */
export const availableAppealDeadlineSelector = createSelector(
  currentExamSelector,
  currentExam => getAvailableAppealDeadline(currentExam)
);

/**
 *  Возвращает список названий экзаменов, по которым доступна апелляция
 */
export const examsListNamesSelector = createSelector(
  examListSelector,
  subState => {
    return subState.reduce((accum, item) => {
      if (getAvailableAppealDeadline(item)) {
        return [...accum, item.subject_name];
      }

      return accum;
    }, []);
  }
);

/**
 *  Возвращает список экзаменов, по которым доступна апелляция
 */
export const examsListAvailableSelector = createSelector(
  examListSelector,
  subState => {
    return subState.reduce((accum, item) => {
      if (getAvailableAppealDeadline(item)) {
        return [...accum, item];
      }

      return accum;
    }, []);
  }
);

export function getDateTime(date, endDay) {
  return date + (endDay ? 'T23:59:59.000+03:00' : 'T00:00:00.000+03:00');
}

export function getTimeSlotDateAndTime(date, time) {
  return `${date}T${time.replace('.0Z', '')}.000+03:00`;
}

/**
 * Возвращает набор данных для отправки на сервер
 */
export const appealSubmitSubjectObjectSelector = createSelector(
  currentExamSelector,
  appealGradesExamSelector,
  timeSlotChatValueSelector,
  availableAppealDeadlineSelector,
  (currentExam, examTasks, timeSlotChat, deadline) => {
    const examTasksSend = examTasks
      /**
       * todo если comment пустой сервер вернет ошибку валидации
       * todo по этой причине буду фильтровать такие итемы
       */
      .filter(item => !!item.comment)
      .map(row => ({
        task_number: row.task_number,
        task_name: row.task_name,
        primary_score: row.max_mark.toString(),
        test_score: row.mark.toString(),
        answer: row.answer,
        is_assessment_correct: true,
        comment: row.comment
          .replace(/(\n{2,})/g, '\n')
          .replace(/(\s{2,})/g, ' '),
        detailed_response: row.detailed
      }));

    return {
      subject_name: currentExam.subject_name,
      subject_code: currentExam.subject_code,
      file_url: currentExam.file,
      exam_type: getConvertedExamType(currentExam),
      exam_date: currentExam.date,
      exam_tasks: examTasksSend,
      time_slot_chat: {
        conv_day_time_begin: getTimeSlotDateAndTime(
          timeSlotChat.conv_day,
          timeSlotChat.conv_time_begin
        ),
        conv_day_time_end: getTimeSlotDateAndTime(
          timeSlotChat.conv_day,
          timeSlotChat.conv_time_end
        )
      },
      protocol: {
        protocol_num: currentExam.protocol_number,
        protocol_date: getDateTime(currentExam.protocol_date)
      },
      appeal_deadline: {
        appeal_deadline_from: getDateTime(deadline.appeal_deadline_from),
        appeal_deadline_to: getDateTime(deadline.appeal_deadline_to, true)
      }
    };
  }
);

/**
 * Возвращает флаг true|false если есть хотя бы один
 * отмеченный и откомментированный итем в таблице заданий
 * и предыдущий шаг тоже готов
 */
export const appealGradesExamSelectedSelector = createSelector(
  appealGradesAllExamSelector,
  subState =>
    subState.length > 0 &&
    subState.every(grade => grade.comment && !grade.invalid)
);

/**
 * Возвращает набор данных для поля выбора времени онлайн чата
 */
export const onlineChatIntervalsSelector = createSelector(
  currentExamSelector,
  subState => subState.chatIntervals || []
);

/**
 * Возвращает наличие экзаменов, для которых возможна подача апелляции
 */

export const examsAvailabilitySelector = createSelector(
  examSelector,
  subState => subState.hasAvailableExams
);
